import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import React from "react";

import { Action, needsWorkActionLabels } from "../actions";

const NEXT_ACTION_OPTIONS = Array.from(needsWorkActionLabels.keys()).filter(
  (action) =>
    ![Action.CALL_PAYER, Action.NEEDS_REVIEW, Action.COMPLETED].includes(
      action,
    ),
);

interface SetNextActionDialogProps {
  claim: ClaimWithProcedureAndPatientMessage | null;
  onClose: () => void;
  onSave: (nextAction: Action) => Promise<void>;
}

export default function SetNextActionDialog({
  claim,
  onClose,
  onSave,
}: SetNextActionDialogProps) {
  const [nextAction, setNextAction] = React.useState<Action>(Action.RESUBMIT);
  const [isSaving, setIsSaving] = React.useState(false);

  const onConfirm = async () => {
    setIsSaving(true);
    try {
      await onSave(nextAction);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={claim !== null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Set next step
        {claim?.wieldyPatientName ? ` — ${claim.wieldyPatientName}` : ""}
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ marginBottom: 2 }}>
          Calling the payer is investigative. Pick the next denial action — the
          claim stays in Needs Work until that action is performed.
        </Alert>
        <FormControl fullWidth sx={{ marginTop: 1 }}>
          <InputLabel id="next-denial-action-label">
            Next Denial Action
          </InputLabel>
          <Select
            labelId="next-denial-action-label"
            label="Next Denial Action"
            value={nextAction}
            onChange={(e) => setNextAction(e.target.value as Action)}
          >
            {NEXT_ACTION_OPTIONS.map((action) => (
              <MenuItem key={action} value={action}>
                {needsWorkActionLabels.get(action)?.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={isSaving}>
          Set next step
        </Button>
      </DialogActions>
    </Dialog>
  );
}
